/* BLANC SPACE CUSTOM — curated video rail and shared Loox media viewer. */

if (!customElements.get('video-feedback')) {
	class VideoFeedback extends HTMLElement {
		connectedCallback() {
			if (this._initialized) return;
			this._initialized = true;

			this.dialog = this.querySelector('[data-vf-dialog]');
			this.track = this.querySelector('[data-vf-track]');
			this.player = this.querySelector('[data-vf-player]');
			this.inlineRail = this.querySelector('.video-feedback__rail');
			// WebKit re-snaps a scroll-snap rail when children are appended, so the
			// Loox circles arriving pushed the rail one or two circles along before
			// the shopper touched it (iPhone, 12 Oct 2026). Remember a real swipe
			// so the reset in setReviewItems never fights the shopper.
			this.inlineRailTouched = false;
			['touchstart', 'wheel', 'pointerdown'].forEach((name) => {
				this.inlineRail?.addEventListener(name, () => { this.inlineRailTouched = true; }, { passive: true, once: true });
			});
			this.modalRail = this.querySelector('.video-feedback__modal-rail');
			this.previousButton = this.querySelector('[data-vf-prev]');
			this.nextButton = this.querySelector('[data-vf-next]');
			this.controls = this.querySelector('.video-feedback__controls');
			this.playButton = this.querySelector('[data-vf-play]');
			this.muteButton = this.querySelector('[data-vf-mute]');
			this.previewVideo = this.querySelector('[data-vf-preview]');
			this.currentIndex = 0;
			this.curatedCount = Number.parseInt(this.dataset.videoCount, 10) || 0;
			this.reviewItems = [];
			this.reviewIndexByKey = new Map();
			this.reviewSource = null;
			this.muted = true;
			this.scrollTimer = null;
			this.modalRailFrame = null;
			this.returnFocusTo = null;
			this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

			this.refreshCollections();
			// Zero curated slides is valid: viewer-only instances (reviews page)
			// start empty and receive review slides via setReviewItems.
			if (!this.dialog || !this.track) return;

			this.querySelectorAll('[data-vf-open]').forEach((button) => {
				button.addEventListener('click', () => this.open(Number(button.dataset.vfOpen), button));
			});

			this.modalRail?.addEventListener('click', this.handleRailClick);
			this.modalRail?.addEventListener('scroll', this.handleModalRailScroll, { passive: true });
			this.querySelector('[data-vf-close]')?.addEventListener('click', () => this.dialog.close());
			this.previousButton?.addEventListener('click', () => this.select(this.currentIndex - 1));
			this.nextButton?.addEventListener('click', () => this.select(this.currentIndex + 1));
			this.playButton?.addEventListener('click', () => this.togglePlayback());
			this.muteButton?.addEventListener('click', () => this.toggleMute());

			this.dialog.addEventListener('click', this.handleDialogClick);
			this.dialog.addEventListener('close', this.handleDialogClose);
			this.track.addEventListener('scroll', this.handleTrackScroll, { passive: true });
			this.track.addEventListener('click', this.handleTrackClick);
			this.track.addEventListener('play', this.handleMediaState, true);
			this.track.addEventListener('pause', this.handleMediaState, true);
			this.track.addEventListener('ended', this.handleMediaState, true);
			document.addEventListener('blancspace:loox-review-media-ready', this.handleReviewMediaReady);

			this.updateState();
			this.initPreview();
			this.initReviewContinuation();
		}

		disconnectedCallback() {
			window.clearTimeout(this.scrollTimer);
			window.cancelAnimationFrame(this.modalRailFrame);
			this.previewObserver?.disconnect();
			this.reviewLoadObserver?.disconnect();
			this.previewVideo?.pause();
			this.pauseAll?.();
			this.modalRail?.removeEventListener('click', this.handleRailClick);
			this.modalRail?.removeEventListener('scroll', this.handleModalRailScroll);
			this.dialog?.removeEventListener('click', this.handleDialogClick);
			this.dialog?.removeEventListener('close', this.handleDialogClose);
			this.track?.removeEventListener('scroll', this.handleTrackScroll);
			this.track?.removeEventListener('click', this.handleTrackClick);
			this.track?.removeEventListener('play', this.handleMediaState, true);
			this.track?.removeEventListener('pause', this.handleMediaState, true);
			this.track?.removeEventListener('ended', this.handleMediaState, true);
			document.removeEventListener('blancspace:loox-review-media-ready', this.handleReviewMediaReady);
			document.documentElement.classList.remove('video-feedback-open');
		}

		handleRailClick = (event) => {
			const button = event.target.closest('[data-vf-select]');
			if (button && this.contains(button)) this.select(Number(button.dataset.vfSelect));
		};

		handleModalRailScroll = () => {
			if (this.modalRailFrame) return;
			this.modalRailFrame = requestAnimationFrame(() => {
				this.modalRailFrame = null;
				this.hydrateVisibleThumbs();
			});
		};

		handleDialogClick = (event) => {
			if (event.target === this.dialog) this.dialog.close();
		};

		handleDialogClose = () => this.onClose();

		handleTrackScroll = () => {
			this.hydrateTrackWindow();
			window.clearTimeout(this.scrollTimer);
			this.scrollTimer = window.setTimeout(() => this.syncFromScroll(), 100);
		};

		handleTrackClick = (event) => {
			const expandToggle = event.target.closest('[data-vf-expand]');
			if (expandToggle) {
				this.toggleReviewExpand(expandToggle);
				return;
			}
			if (event.target.closest('[data-vf-review-bubble]')) return;
			if (event.target.closest('[data-vf-video]')) this.togglePlayback();
		};

		toggleReviewExpand(button) {
			const bubble = button.closest('[data-vf-review-bubble]');
			if (!bubble) return;
			const expanded = bubble.toggleAttribute('data-vf-expanded');
			button.setAttribute('aria-expanded', String(expanded));
			button.setAttribute('aria-label', expanded ? 'Collapse review' : 'Read full review');
			if (!expanded) bubble.scrollTop = 0;
			this.syncReviewBubbleHeight();
		}

		handleMediaState = (event) => {
			if (event.target === this.getActiveVideo()) this.updatePlaybackButton();
		};

		handleReviewMediaReady = (event) => {
			if (!this.hasAttribute('data-review-continuation')) return;
			this.setReviewItems(event.detail?.items || [], event.detail?.source || null);
		};

		requestReviewItems() {
			if (!this.hasAttribute('data-review-continuation')) return;
			const reviewSection = document.querySelector('loox-api-reviews');
			reviewSection?.loadInitial?.();
		}

		initReviewContinuation() {
			if (!this.hasAttribute('data-review-continuation')) return;
			const reviewSection = document.querySelector('loox-api-reviews');
			if (reviewSection?.viewerItems?.length) {
				this.setReviewItems(reviewSection.getSharedViewerItems(), reviewSection);
				return;
			}

			const request = () => customElements.whenDefined('loox-api-reviews').then(() => this.requestReviewItems());
			if (!('IntersectionObserver' in window)) {
				request();
				return;
			}

			this.reviewLoadObserver = new IntersectionObserver(([entry]) => {
				if (!entry.isIntersecting) return;
				this.reviewLoadObserver.disconnect();
				request();
			}, { rootMargin: '400px 0px' });
			this.reviewLoadObserver.observe(this.inlineRail || this);
		}

		keepInlineRailAtStart() {
			const rail = this.inlineRail;
			if (!rail || this.inlineRailTouched) return;
			// Twice: WebKit applies the re-snap after layout, not at append time.
			const reset = () => { if (!this.inlineRailTouched && rail.scrollLeft !== 0) rail.scrollTo({ left: 0, behavior: 'auto' }); };
			requestAnimationFrame(reset);
			window.setTimeout(reset, 300);
		}

		refreshCollections() {
			this.slides = [...this.querySelectorAll('[data-vf-slide]')];
			this.tabs = [...this.querySelectorAll('[data-vf-select]')];
			this.videos = [...this.querySelectorAll('[data-vf-video]')];
		}

		setReviewItems(items, source = null, centerKey = null) {
			if (!this.hasAttribute('data-review-continuation') || !Array.isArray(items)) return;
			// Never mutate the dialog subtree while it sits in the top layer:
			// injecting the review slides into the open modal kills the tab on
			// iOS WebKit. Stash the items and apply them on close.
			if (this.dialog?.open) {
				this._pendingReviewItems = { items, source };
				return;
			}

			// The full Loox pool can run to 100+ media items; promoting a dialog
			// holding all of them into the top layer (110 slides / ~2,400 nodes
			// measured) crashes iOS WebKit. Keep the full pool in memory but
			// render only a small window of slides/thumbs, centred on the review
			// being opened when it comes from the Loox cards.
			this.allReviewItems = items;
			const MAX_RENDERED = 12;
			let windowItems = items;
			if (items.length > MAX_RENDERED) {
				let start = 0;
				if (centerKey) {
					const centerIndex = items.findIndex((item) => item.key === centerKey);
					if (centerIndex >= 0) {
						start = Math.max(0, Math.min(centerIndex - Math.floor(MAX_RENDERED / 2), items.length - MAX_RENDERED));
					}
				}
				windowItems = items.slice(start, start + MAX_RENDERED);
			}

			const signature = windowItems.map((item) => item.key).join('|');
			if (signature === this.reviewSignature) return;

			this.reviewSignature = signature;
			this.reviewItems = windowItems;
			this.reviewSource = source;
			this.reviewIndexByKey.clear();
			this.querySelectorAll('[data-vf-loox-review]').forEach((node) => node.remove());
			this.querySelectorAll('[data-vf-loox-inline]').forEach((node) => node.remove());

			const inlineReviewIds = new Set();
			items.forEach((item) => {
				const reviewId = item.reviewId || item.key.split(':')[0];
				if (inlineReviewIds.has(reviewId) || inlineReviewIds.size >= 7) return;
				inlineReviewIds.add(reviewId);
				this.inlineRail?.append(this.createInlineReviewThumb(item));
			});
			this.keepInlineRailAtStart();

			windowItems.forEach((item, itemIndex) => {
				const index = this.curatedCount + itemIndex;
				this.reviewIndexByKey.set(item.key, index);
				this.modalRail?.append(this.createReviewThumb(item, index));
				this.track?.append(this.createReviewSlide(item, index));
			});

			this.refreshCollections();
			this.currentIndex = Math.min(this.currentIndex, this.slides.length - 1);
			this.updateState();
			if (this.dialog?.open) {
				this.hydrateSlide(this.currentIndex);
				requestAnimationFrame(() => this.hydrateVisibleThumbs());
			}
		}

		// The Loox API hands back `_orig` assets (~700KB+); the suffix-less
		// variant (~30KB) is plenty for these small circles. onerror falls
		// back to the original URL in case a review lacks the base variant.
		smallLooxThumb(image, url) {
			const small = /images\.loox\.io\//.test(url) ? url.replace(/_orig(\.[a-z]+)(\?|$)/i, '$1$2') : url;
			if (small !== url) image.addEventListener('error', () => { image.src = url; }, { once: true });
			return small;
		}

		createInlineReviewThumb(item) {
			const button = this.createElement('button', 'video-feedback__thumb video-feedback__thumb--inline video-feedback__thumb--review');
			button.type = 'button';
			button.dataset.vfLooxInline = '';
			// No role="listitem": there is no list parent and the explicit role hid the
			// button semantics from assistive tech (2026-09-07 accessibility follow-up).
			button.setAttribute('aria-label', `Open ${item.type === 'video' ? 'video' : 'photo'} review from ${item.reviewerName}`);

			const media = this.createElement('span', 'video-feedback__thumb-media');
			if (item.thumbnailUrl) {
				const image = document.createElement('img');
				image.src = this.smallLooxThumb(image, item.thumbnailUrl);
				image.alt = '';
				image.loading = 'eager';
				image.decoding = 'async';
				media.append(image);
			} else {
				media.append(this.createElement('span', 'video-feedback__thumb-placeholder'));
			}
			button.append(media);
			if (item.type === 'video') button.append(this.createPlayBadge());
			button.addEventListener('click', () => this.openReview(item.key, button));
			return button;
		}

		createReviewThumb(item, index) {
			const button = this.createElement('button', 'video-feedback__thumb video-feedback__thumb--modal video-feedback__thumb--review');
			button.type = 'button';
			button.dataset.vfSelect = String(index);
			button.dataset.vfLooxReview = '';
			button.setAttribute('role', 'tab');
			button.setAttribute('aria-selected', 'false');
			button.setAttribute('aria-label', `${item.reviewerName} review ${index + 1}`);

			const media = this.createElement('span', 'video-feedback__thumb-media');
			if (item.thumbnailUrl) {
				const image = document.createElement('img');
				image.dataset.vfSrc = this.smallLooxThumb(image, item.thumbnailUrl);
				image.alt = '';
				image.loading = 'lazy';
				image.decoding = 'async';
				media.append(image);
			} else {
				media.append(this.createElement('span', 'video-feedback__thumb-placeholder'));
			}
			button.append(media);
			if (item.type === 'video') button.append(this.createPlayBadge());
			return button;
		}

		createReviewSlide(item, index) {
			const slide = this.createElement('article', 'video-feedback__slide video-feedback__slide--review');
			slide.dataset.vfSlide = String(index);
			slide.dataset.vfLooxReview = '';
			slide.dataset.vfReviewKey = item.key;
			slide.setAttribute('role', 'tabpanel');
			slide.setAttribute('aria-label', `${item.reviewerName} review ${index + 1}`);
			slide.setAttribute('aria-hidden', 'true');

			if (item.type === 'video') {
				// No <video> element until the slide is actually viewed: a dialog
				// subtree dense with media elements crashes iOS WebKit when it is
				// promoted to the top layer. The slot materializes on demand.
				slide.append(this.createVideoSlot(item.url, item.thumbnailUrl));
			} else {
				const image = document.createElement('img');
				image.className = 'video-feedback__image';
				image.dataset.vfSrc = item.url;
				image.alt = `Photo shared by ${item.reviewerName}`;
				image.loading = 'lazy';
				image.decoding = 'async';
				slide.append(image);
			}

			slide.append(this.createReviewBubble(item));
			return slide;
		}

		createVideoSlot(url, poster) {
			const slot = this.createElement('div', 'video-feedback__video video-feedback__video-slot');
			slot.dataset.vfVideoSlot = '';
			slot.dataset.vfUrl = url;
			if (poster) {
				slot.dataset.vfPoster = poster;
				slot.style.backgroundImage = `url("${poster}")`;
			}
			return slot;
		}

		materializeVideo(slot) {
			const video = document.createElement('video');
			video.className = 'video-feedback__video';
			video.dataset.vfVideo = '';
			video.dataset.vfMaterialized = '';
			video.dataset.vfUrl = slot.dataset.vfUrl;
			video.muted = true;
			video.loop = true;
			video.playsInline = true;
			video.preload = 'none';
			if (slot.dataset.vfPoster) {
				video.dataset.vfPoster = slot.dataset.vfPoster;
				video.poster = slot.dataset.vfPoster;
			}
			const source = document.createElement('source');
			source.dataset.src = slot.dataset.vfUrl;
			source.type = 'video/mp4';
			video.append(source);
			slot.replaceWith(video);
			return video;
		}

		dematerializeVideo(video) {
			video.pause();
			video.replaceWith(this.createVideoSlot(video.dataset.vfUrl, video.dataset.vfPoster));
		}

		createReviewBubble(item) {
			// Compact card: name + verified left, stars right, no date. The copy
			// clamps to a few fading lines with a chevron to expand — all state
			// changes are attribute flips on these nodes, never insertions,
			// because the dialog subtree must not be mutated while open.
			const bubble = this.createElement('div', 'video-feedback__review-bubble');
			bubble.dataset.vfReviewBubble = '';
			const meta = this.createElement('div', 'video-feedback__review-meta');
			meta.append(this.createElement('strong', '', item.reviewerName));
			if (item.verified) meta.append(this.createVerifiedBadge());
			const stars = this.createElement('span', 'video-feedback__review-stars', '★'.repeat(item.rating));
			stars.setAttribute('role', 'img');
			stars.setAttribute('aria-label', `${item.rating} out of 5 stars`);
			meta.append(stars);
			bubble.append(meta);

			if (item.body) {
				const copy = this.createReviewCopy(item);
				copy.dataset.vfReviewCopy = '';
				bubble.append(copy);
				const expand = this.createElement('button', 'video-feedback__review-expand');
				expand.type = 'button';
				expand.dataset.vfExpand = '';
				expand.setAttribute('aria-expanded', 'false');
				expand.setAttribute('aria-label', 'Read full review');
				expand.innerHTML = '<svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 4.5 6 8.5l4-4"/></svg>';
				bubble.append(expand);
			}
			if (item.product) bubble.append(this.createReviewProduct(item.product));
			return bubble;
		}

		createReviewCopy(item) {
			const paragraph = this.createElement('p', 'video-feedback__review-copy');
			const body = item.body || '';
			const highlights = Array.isArray(item.highlights) ? item.highlights : [];
			const ranges = highlights.map((highlight) => {
				const start = body.toLocaleLowerCase().indexOf(highlight.toLocaleLowerCase());
				return { start, end: start + highlight.length };
			}).filter((range) => range.start >= 0).sort((a, b) => a.start - b.start);

			if (!ranges.length) {
				paragraph.textContent = body;
				return paragraph;
			}

			let cursor = 0;
			ranges.forEach((range) => {
				if (range.start < cursor) return;
				if (range.start > cursor) paragraph.append(document.createTextNode(body.slice(cursor, range.start)));
				paragraph.append(this.createElement('mark', 'video-feedback__review-highlight', body.slice(range.start, range.end)));
				cursor = range.end;
			});
			if (cursor < body.length) paragraph.append(document.createTextNode(body.slice(cursor)));
			return paragraph;
		}

		createVerifiedBadge() {
			const badge = this.createElement('span', 'video-feedback__review-verified');
			badge.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 1.8 12 4l2.9-.1.5 2.8L18 8l-1.2 2.6.9 2.7-2.7 1-.7 2.8-2.8-.3L10 19l-1.5-2.2-2.8.3-.7-2.8-2.7-1 .9-2.7L2 8l2.6-1.3.5-2.8L8 4Z" fill="currentColor"/><path d="m6.7 10.1 2 2 4.6-4.5" fill="none" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Verified purchase</span>';
			return badge;
		}

		createReviewProduct(product) {
			const reference = this.createElement(product.url ? 'a' : 'div', 'video-feedback__review-product');
			if (product.url) reference.href = product.url;
			if (product.imageUrl) {
				const image = document.createElement('img');
				image.dataset.vfSrc = product.imageUrl;
				image.alt = '';
				image.loading = 'lazy';
				reference.append(image);
			}
			const copy = this.createElement('span');
			copy.append(this.createElement('small', '', 'Review for'));
			copy.append(this.createElement('strong', '', product.name));
			reference.append(copy);
			return reference;
		}

		createPlayBadge() {
			const badge = this.createElement('span', 'video-feedback__thumb-play');
			badge.setAttribute('aria-hidden', 'true');
			badge.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16"><path d="m9 6 9 6-9 6V6Z" fill="currentColor"/></svg>';
			return badge;
		}

		createElement(tagName, className = '', text = '') {
			const element = document.createElement(tagName);
			if (className) element.className = className;
			if (text) element.textContent = text;
			return element;
		}

		hydrateImage(image) {
			if (!image?.dataset.vfSrc || image.hasAttribute('src')) return;
			image.src = image.dataset.vfSrc;
		}

		hydrateSlide(index) {
			const slide = this.slides[index];
			slide?.querySelectorAll('[data-vf-video-slot]').forEach((slot) => this.materializeVideo(slot));
			slide?.querySelectorAll('img[data-vf-src]').forEach((image) => this.hydrateImage(image));
			slide?.querySelectorAll('video[data-vf-poster]').forEach((video) => {
				if (!video.hasAttribute('poster')) video.poster = video.dataset.vfPoster;
			});
		}

		hydrateTrackWindow() {
			if (!this.track?.clientWidth) return;
			const position = this.track.scrollLeft / this.track.clientWidth;
			this.hydrateSlide(Math.floor(position));
			this.hydrateSlide(Math.ceil(position));
		}

		hydrateVisibleThumbs() {
			if (!this.dialog?.open || !this.modalRail) return;
			const railRect = this.modalRail.getBoundingClientRect();
			const preloadInset = 120;

			this.modalRail.querySelectorAll('img[data-vf-src]').forEach((image) => {
				const thumbRect = image.closest('[data-vf-select]')?.getBoundingClientRect();
				if (!thumbRect) return;
				const isNearViewport = thumbRect.right >= railRect.left - preloadInset
					&& thumbRect.left <= railRect.right + preloadInset;

				if (isNearViewport) this.hydrateImage(image);
				else image.removeAttribute('src');
			});
		}

		releaseDistantSlideMedia(activeIndex) {
			this.slides.forEach((slide, index) => {
				if (Math.abs(index - activeIndex) <= 2) return;
				slide.querySelectorAll('img[data-vf-src][src]').forEach((image) => image.removeAttribute('src'));
				slide.querySelectorAll('video[data-vf-poster][poster]').forEach((video) => video.removeAttribute('poster'));
				slide.querySelectorAll('video[data-vf-materialized]').forEach((video) => this.dematerializeVideo(video));
				slide.querySelectorAll('video[data-vf-video][data-loaded="true"]').forEach((video) => {
					video.pause();
					video.querySelectorAll('source[data-src][src]').forEach((source) => source.removeAttribute('src'));
					delete video.dataset.loaded;
					video.load();
				});
			});
		}

		openReview(key, opener) {
			let index = this.reviewIndexByKey.get(key);
			if (typeof index !== 'number' && Array.isArray(this.allReviewItems) && !this.dialog?.open) {
				// The requested review sits outside the rendered window: rebuild
				// the window centred on it before opening.
				this.setReviewItems(this.allReviewItems, this.reviewSource, key);
				index = this.reviewIndexByKey.get(key);
			}
			if (typeof index !== 'number') return;
			this.open(index, opener);
		}

		open(index, opener) {
			this.returnFocusTo = opener;
			this.previewVideo?.pause();
			this.muted = true;
			this.updateMuteButton();
			this.requestReviewItems();

			if (!this.dialog.open) this.dialog.showModal();
			document.documentElement.classList.add('video-feedback-open');

			requestAnimationFrame(() => {
				this.select(index, { behavior: 'auto', restart: true });
				this.hydrateVisibleThumbs();
				this.querySelector('[data-vf-close]')?.focus({ preventScroll: true });
			});
		}

		onClose() {
			window.clearTimeout(this.scrollTimer);
			this.pauseAll();
			if (this._pendingReviewItems) {
				const pending = this._pendingReviewItems;
				this._pendingReviewItems = null;
				this.setReviewItems(pending.items, pending.source);
			}
			document.documentElement.classList.remove('video-feedback-open');
			this.returnFocusTo?.focus({ preventScroll: true });
			this.returnFocusTo = null;
			this.syncPreviewPlayback();
		}

		initPreview() {
			if (!this.previewVideo || this.reduceMotion || !('IntersectionObserver' in window)) return;

			this.previewObserver = new IntersectionObserver(([entry]) => {
				this.previewVisible = entry.isIntersecting;
				this.syncPreviewPlayback();
			}, { threshold: 0.35 });
			this.previewObserver.observe(this.previewVideo);
		}

		loadPreview() {
			if (!this.previewVideo || this.previewVideo.dataset.loaded === 'true') return;
			this.previewVideo.querySelectorAll('source[data-src]').forEach((source) => {
				source.src = source.dataset.src;
			});
			this.previewVideo.dataset.loaded = 'true';
			this.previewVideo.load();
		}

		syncPreviewPlayback() {
			if (!this.previewVideo) return;
			if (this.previewVisible && !this.dialog.open && !this.reduceMotion) {
				this.loadPreview();
				this.previewVideo.muted = true;
				const promise = this.previewVideo.play();
				if (promise) promise.catch(() => {});
			} else {
				this.previewVideo.pause();
			}
		}

		select(index, options = {}) {
			const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
			const changed = nextIndex !== this.currentIndex;
			this.currentIndex = nextIndex;

			this.pauseAll();
			this.releaseDistantSlideMedia(nextIndex);
			this.hydrateSlide(nextIndex);
			this.tabs[nextIndex]?.querySelectorAll('img[data-vf-src]').forEach((image) => this.hydrateImage(image));
			this.updateState();

			if (options.scroll !== false) {
				this.track.scrollTo({
					left: this.track.clientWidth * nextIndex,
					behavior: options.behavior || 'auto'
				});
			}

			const selectedTab = this.tabs[nextIndex];
			selectedTab?.scrollIntoView({
				behavior: this.reduceMotion ? 'auto' : 'smooth',
				block: 'nearest',
				inline: 'center'
			});
			requestAnimationFrame(() => this.hydrateVisibleThumbs());

			this.startVideo(this.getActiveVideo(), options.restart !== false || changed);
		}

		syncFromScroll() {
			if (!this.dialog.open || !this.track.clientWidth) return;
			const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
			if (index !== this.currentIndex) this.select(index, { scroll: false, restart: true });
		}

		loadVideo(video) {
			if (!video || video.dataset.loaded === 'true') return;
			video.querySelectorAll('source[data-src]').forEach((source) => {
				source.src = source.dataset.src;
			});
			video.dataset.loaded = 'true';
			video.load();
		}

		startVideo(video, restart) {
			if (!video) return;
			this.loadVideo(video);
			video.muted = this.muted;

			const play = () => {
				if (!this.dialog.open || video !== this.getActiveVideo()) return;
				if (restart) {
					try { video.currentTime = 0; } catch (_) { /* Metadata is not ready yet. */ }
				}
				const promise = video.play();
				if (promise) promise.catch(() => this.updatePlaybackButton());
			};

			if (video.readyState >= 2) play();
			else video.addEventListener('canplay', play, { once: true });
		}

		getActiveVideo() {
			return this.slides[this.currentIndex]?.querySelector('[data-vf-video]') || null;
		}

		pauseAll() {
			this.querySelectorAll('[data-vf-video]').forEach((video) => video.pause());
			this.updatePlaybackButton();
		}

		togglePlayback() {
			const video = this.getActiveVideo();
			if (!video) return;
			if (video.paused) this.startVideo(video, false);
			else video.pause();
		}

		toggleMute() {
			const video = this.getActiveVideo();
			if (!video) return;
			this.muted = !this.muted;
			// Live query: review videos are materialized after this.videos was built.
			this.querySelectorAll('[data-vf-video]').forEach((item) => { item.muted = this.muted; });
			this.updateMuteButton();
		}

		updateState() {
			this.tabs.forEach((tab, index) => tab.setAttribute('aria-selected', String(index === this.currentIndex)));
			this.slides.forEach((slide, index) => {
				const inactive = index !== this.currentIndex;
				slide.setAttribute('aria-hidden', String(inactive));
				slide.toggleAttribute('inert', inactive);
			});
			if (this.previousButton) this.previousButton.disabled = this.currentIndex === 0;
			if (this.nextButton) this.nextButton.disabled = this.currentIndex === this.slides.length - 1;

			const currentSlide = this.slides[this.currentIndex];
			const reviewBubble = currentSlide?.querySelector('[data-vf-review-bubble]');
			// Leaving a slide collapses its card so every slide re-enters compact.
			this.slides.forEach((slide, index) => {
				if (index === this.currentIndex) return;
				const bubble = slide.querySelector('[data-vf-review-bubble][data-vf-expanded]');
				if (!bubble) return;
				bubble.removeAttribute('data-vf-expanded');
				const toggle = bubble.querySelector('[data-vf-expand]');
				toggle?.setAttribute('aria-expanded', 'false');
				toggle?.setAttribute('aria-label', 'Read full review');
			});
			this.player?.classList.toggle('has-product', Boolean(currentSlide?.querySelector('[data-vf-product]')));
			this.player?.classList.toggle('has-review', Boolean(reviewBubble));
			if (this.controls) this.controls.hidden = !this.getActiveVideo();
			this.syncReviewBubbleHeight();
			this.updatePlaybackButton();
			this.updateMuteButton();
		}

		syncReviewBubbleHeight() {
			const bubble = this.slides[this.currentIndex]?.querySelector('[data-vf-review-bubble]');
			const copy = bubble?.querySelector('[data-vf-review-copy]');
			if (copy && !bubble.hasAttribute('data-vf-expanded')) {
				bubble.toggleAttribute('data-vf-overflow', copy.scrollHeight > copy.clientHeight + 1);
			}
			const measure = () => this.player?.style.setProperty('--vf-review-bubble-height', `${bubble?.offsetHeight || 0}px`);
			measure();
			requestAnimationFrame(measure);
		}

		updatePlaybackButton() {
			if (!this.playButton) return;
			const video = this.getActiveVideo();
			const playing = Boolean(video && !video.paused && !video.ended);
			this.playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
			this.playButton.setAttribute('aria-pressed', String(playing));
			this.playButton.querySelector('[data-vf-pause-icon]').hidden = !playing;
			this.playButton.querySelector('[data-vf-play-icon]').hidden = playing;
		}

		updateMuteButton() {
			if (!this.muteButton) return;
			this.muteButton.setAttribute('aria-label', this.muted ? 'Unmute video' : 'Mute video');
			this.muteButton.setAttribute('aria-pressed', String(this.muted));
			this.muteButton.querySelector('[data-vf-muted-icon]').hidden = !this.muted;
			this.muteButton.querySelector('[data-vf-sound-icon]').hidden = this.muted;
		}
	}

	customElements.define('video-feedback', VideoFeedback);
}
